// Pure display + sort logic for the Tickets sub-nav (AL-126). No DOM / state
// imports, so it's unit-testable under Node — the same split router-url.ts and
// connector-status.ts use.
//
// Two of these functions exist for one reason: a ticket row is routinely
// INCOMPLETE, and the misleading rendering of an absence is worse than an
// obvious one. The dogfood DB hits both cases on first render:
//
//   no title   the connector never fetched details for that key (a key seen in
//              a transcript mints a row immediately; the title arrives later,
//              or never — AL-34 is a false-positive key that has no issue).
//   no cost    59 of 91 listed tickets have no block attributed to them, so
//              nothing measured their spend. `$0.00` would assert the work was
//              free, which is a different and false claim (the AL-151 class of
//              bug: a missing price read as zero).
//
// Neither is an error state — they are the normal condition of a ticket the
// pipeline has only partly seen — so both read as plain prose, not a warning.

/** The row shape the ticket table consumes (a subset of the store's ArtifactListItem). */
export interface TicketRow {
  id: string;
  kind?: string;
  ident: string | null;
  title: string | null;
  status: string | null;
  externalId: string | null;
  sessions: number;
  costUsd: number;
  /** False when NO block carries this ticket's cost — `costUsd` is then a 0 default. */
  costMeasured: boolean;
  storyPoints: number | null;
  createdAt: string | null;
  completedAt: string | null;
  /** Resolved parent artifact id; null when the parent was never ingested. */
  parentId?: string | null;
  /** Parent's Jira key from connector details — survives an un-ingested parent. */
  parentKey?: string | null;
  /** Jira's own issue-type name ('Epic', 'Story', 'Subtask', …), or null if unfetched. */
  issueType?: string | null;
}

/** One row of the flattened tree: the row plus its depth, for indentation. */
export interface TicketTreeEntry {
  node: TicketRow;
  depth: number;
}

// The four normalized lifecycle states (packages/core jira-ticket.ts TicketState),
// spelled for a reader. `wont_do` is included deliberately: it's terminal but NOT
// success, and the epic tree has real ones (AL-109's whole phase), so collapsing it
// into "Done" would report abandoned work as delivered.
export var TICKET_STATUS_LABELS: Record<string, string> = {
  open: 'Open', in_progress: 'In progress', done: 'Done', wont_do: "Won't do",
};

/**
 * Status → badge class + label. `done` is the only success reading; `wont_do`
 * takes the neutral grey rather than the red failure colour — it was a decision,
 * not a failure. An unknown or absent status stays null-grey and reads as a dash,
 * which is honest: a minted-but-never-fetched ticket has no known state.
 */
export function ticketStatusBadge(status: string | null): { cls: string; label: string } {
  var s = status || '';
  if (s === 'done') return { cls: 'b-success', label: TICKET_STATUS_LABELS.done! };
  if (s === 'in_progress') return { cls: 'b-partial', label: TICKET_STATUS_LABELS.in_progress! };
  if (s === 'open') return { cls: 'b-unknown', label: TICKET_STATUS_LABELS.open! };
  if (s === 'wont_do') return { cls: 'b-null', label: TICKET_STATUS_LABELS.wont_do! };
  return { cls: 'b-null', label: s || '—' };
}

/**
 * What to print in the title cell. A ticket with no title must still read as a
 * ticket — AL-92 is simultaneously untitled and the single most expensive row in
 * the DB ($419.96), so it lands at the top of a cost sort, and a blank cell there
 * would look like a rendering bug rather than missing connector data.
 */
export function ticketTitleText(row: TicketRow): string {
  if (row.title) return row.title;
  return 'No title — not fetched from Jira';
}

/** True when the title cell is showing the placeholder above (so it can be styled muted). */
export function ticketTitleIsPlaceholder(row: TicketRow): boolean {
  return !row.title;
}

/**
 * The cost cell. Unmeasured reads as prose, never as a figure — see the header.
 * A MEASURED zero still prints `$0.00`, because that is a real (if unusual)
 * finding: blocks exist and they carry no priced usage.
 */
export function ticketCostText(row: TicketRow): string {
  if (!row.costMeasured) return 'not measured';
  return '$' + (Number(row.costUsd) || 0).toFixed(2);
}

/** Story points cell: a dash when unset, so an absent estimate isn't read as zero points. */
export function ticketPointsText(row: TicketRow): string {
  return row.storyPoints == null ? '—' : String(row.storyPoints);
}

/**
 * The ticket table's columns, `[key, label, numeric?]` — the same tuple shape as
 * PR_COLS.
 *
 * Lives here rather than beside the renderer so it can be asserted in a unit test:
 * the body row in `renderTicketTable` is hand-written rather than mapped over this
 * list, so a column added here without a matching `<td>` (or vice versa) shears
 * every cell after it by one — values land under the wrong headers, which looks
 * like bad data rather than a layout bug. The test pins the order; this comment is
 * the reason it exists.
 *
 * Points is a PER-ROW figure and never totalled. Jira stores a parent's estimate
 * independently of its children (a subtask can exceed its parent, and there is no
 * Σ story-points field), and this table lists epics, stories and subtasks
 * together — so summing the column would add a parent to its own children, the
 * same double-count AL-129 fixed for cost.
 */
export var TICKET_COLS: Array<[string, string, number]> = [
  ['key', 'Ticket', 0], ['status', 'Status', 0], ['sessions', 'Sessions', 1],
  ['cost', 'Cost', 1], ['points', 'Points', 1], ['created', 'Created', 1],
];

/**
 * Sort key for a column, mirroring the PR table's `prVal` contract with one
 * addition: an UNMEASURED cost returns null, and null sorts last in BOTH
 * directions (see ticketSort). Folding it to 0 would rank 59 unmeasured tickets
 * as the cheapest work in the org on an ascending sort — the same false claim the
 * `$0.00` cell was fixed to avoid.
 */
export function ticketVal(r: TicketRow, col: string): string | number | null {
  if (col === 'key') return (r.ident || '').toLowerCase();
  if (col === 'title') return (r.title || '').toLowerCase();
  if (col === 'status') return statusRank(r.status);
  if (col === 'sessions') return r.sessions || 0;
  if (col === 'cost') return r.costMeasured ? r.costUsd || 0 : null;
  if (col === 'points') return r.storyPoints;
  if (col === 'created') return r.createdAt || '';
  return '';
}

// Status sorts by lifecycle position, not alphabetically — "Done, In progress,
// Open" is an arbitrary order to a reader, whereas open→done reads as progress.
var STATUS_ORDER: Record<string, number> = { open: 1, in_progress: 2, done: 3, wont_do: 4 };
function statusRank(status: string | null): number {
  return STATUS_ORDER[status || ''] ?? 0;
}

/**
 * Sort rows in place by `col`/`dir`. Nulls (unmeasured cost, absent story points)
 * always sink to the bottom regardless of direction — they are absences, and an
 * absence has no position on the scale being sorted. This matches the store's
 * `NULLS LAST` on the same column.
 */
export function ticketSort(rows: TicketRow[], col: string, dir: 'asc' | 'desc'): TicketRow[] {
  var mul = dir === 'asc' ? 1 : -1;
  return rows.sort(function (a, b) {
    var x = ticketVal(a, col), y = ticketVal(b, col);
    if (x == null && y == null) return 0;
    if (x == null) return 1;
    if (y == null) return -1;
    return x < y ? -mul : x > y ? mul : 0;
  });
}

/**
 * Split the rows into the measured population and a count of what that hides.
 *
 * "Not measured" (no block of any session attributed to the row or anything
 * beneath it) is most of the table on a real corpus — sighted-but-never-charged
 * tickets outnumber charged ones — and a reader scanning for spend has to wade
 * through rows that by definition carry none. So the table shows the measured
 * population by default and offers the remainder behind a counted toggle,
 * rather than deleting it: the sighted-but-never-charged population is a real
 * fact this table exists to be ABLE to show (a ticket in the team's orbit whose
 * work never ran through a session), it just isn't the default question.
 *
 * Hierarchy-safe by the cost rollup's own invariant: a row's cost sums its
 * SUBTREE, so an unmeasured row has no measured descendant — filtering the
 * unmeasured can never orphan a measured child from its ancestors. (If that
 * rollup ever changed, this filter would strand children as roots — visible
 * immediately, since ticketTree surfaces orphans rather than dropping them.)
 */
export function ticketFilterMeasured(rows: TicketRow[]): { kept: TicketRow[]; hidden: number } {
  var kept = rows.filter(function (r) { return r.costMeasured; });
  return { kept: kept, hidden: rows.length - kept.length };
}

/**
 * Free-text match, the same fields the server's artifact search covers: key,
 * title and the browse URL. Case-insensitive; an empty query matches everything.
 */
export function ticketMatches(r: TicketRow, q: string): boolean {
  var term = (q || '').trim().toLowerCase();
  if (!term) return true;
  var hay = ((r.ident || '') + ' ' + (r.title || '') + ' ' + (r.externalId || '')).toLowerCase();
  return hay.indexOf(term) !== -1;
}

// ---- Hierarchy ---------------------------------------------------------------
//
// Jira's shape is epic → story → subtask, and the table shows it that way: a user
// reasons about a phase, not about 93 flat keys. One wrinkle makes this more than a
// groupBy: a parent can be REFERENCED but never ingested — 4 keys in the dogfood DB
// (AL-106, AL-90, AL-1, AG-1) are named by children whose parent row may be absent.
// Grouping by `parentKey` as well as `parentId` keeps those children together under
// one branch instead of scattering them as roots.
//
// (It used to be two wrinkles: an epic was stored as `kind='feature'`, so `parentId`
// on a story pointed at a feature id and the store had to list Jira features
// alongside tickets to get both ends of that edge into one array. An epic is a ticket
// now — design §3b — so the edge never leaves the kind.)

/** Normalized hierarchy rank for an issue type: epic 0, story 1, subtask 2, unknown 3. */
export function issueTypeRank(issueType: string | null | undefined): number {
  var t = (issueType || '').toLowerCase().replace(/[\s_-]/g, '');
  if (t === 'epic' || t === 'workstream' || t === 'initiative') return 0;
  if (t === 'story' || t === 'task' || t === 'bug' || t === 'feature' || t === 'improvement') return 1;
  if (t === 'subtask') return 2;
  return 3;
}

/**
 * Short label for the type pill; '' when Jira never told us the type.
 *
 * There is no `kind`-based fallback any more. There used to be one — a Jira-sourced
 * `kind='feature'` row could only be an epic, so it was labelled "Epic" even with no
 * details — and it is now unreachable: an epic is a `kind='ticket'` row like every
 * other issue (design §3b), so `issueType` is the only thing that knows. A ticket
 * with no type stays unlabelled rather than guessing.
 */
export function issueTypeLabel(row: TicketRow): string {
  return row.issueType || '';
}

/** One drawer ticket chip: the linked artifact plus its indent depth. */
export interface DrawerTicket {
  id: string;
  parentArtifactId?: string | null;
  _depth?: number;
}

/**
 * Parent-before-child order + depth for the session drawer's ticket chips (AL-128).
 *
 * A sibling of `ticketTree` above, deliberately NOT the same function: that one ranks
 * siblings by the table's active column sort and resolves parents by key OR id, both
 * of which the drawer has no use for (no sort controls, and the payload carries a
 * real `parentArtifactId`). Sharing it would mean passing a fake sort column.
 *
 * Only edges INSIDE this session's own set are drawn — a parent the session never
 * touched isn't shown, so its child renders as a root rather than indented under
 * nothing. Same rule the attribution prompt uses: the tree is a view of THIS
 * session's work, not a slice of the backlog. Cycle-safe via `seen`.
 */
export function drawerTicketTree<T extends DrawerTicket>(items: T[]): T[] {
  var present: Record<string, boolean> = {};
  items.forEach(function (t) { present[t.id] = true; });
  var children: Record<string, T[]> = {};
  items.forEach(function (t) {
    var p = t.parentArtifactId;
    var b = p && present[p] && p !== t.id ? p : '';
    (children[b] = children[b] || []).push(t);
  });
  var out: T[] = [], seen: Record<string, boolean> = {};
  function walk(bucket: string, depth: number): void {
    (children[bucket] || []).forEach(function (t) {
      if (seen[t.id]) return;
      seen[t.id] = true;
      t._depth = depth;
      out.push(t);
      walk(t.id, depth + 1);
    });
  }
  walk('', 0);
  // Whatever a parent cycle stranded still gets listed, at the root.
  items.forEach(function (t) {
    if (seen[t.id]) return;
    seen[t.id] = true;
    t._depth = 0;
    out.push(t);
  });
  return out;
}

/** The key a row's children reference: its own Jira key, falling back to the id. */
function selfKeys(r: TicketRow): string[] {
  var out = [r.id];
  if (r.ident) out.push(r.ident);
  return out;
}

/**
 * Flatten rows into parent-before-child order with a depth for each, ordered
 * WITHIN each sibling group by the active column sort. The sort therefore ranks
 * siblings, never reparents them — a tree whose rows jump branches on sort is not
 * a tree. Rows whose parent is not in the list surface as roots (never dropped:
 * a search that matches only a subtask must still show it).
 */
export function ticketTree(rows: TicketRow[], col: string, dir: 'asc' | 'desc'): TicketTreeEntry[] {
  var byKey: Record<string, TicketRow> = {};
  rows.forEach(function (r) { selfKeys(r).forEach(function (k) { byKey[k] = r; }); });

  // parent bucket key '' = a root. Resolve via parentId first (an actual row id),
  // then parentKey (survives an un-ingested parent).
  function parentBucket(r: TicketRow): string {
    var pid = r.parentId;
    if (pid && byKey[pid] && byKey[pid] !== r) return byKey[pid]!.id;
    var pk = r.parentKey;
    if (pk && byKey[pk] && byKey[pk] !== r) return byKey[pk]!.id;
    return '';
  }

  var children: Record<string, TicketRow[]> = {};
  rows.forEach(function (r) {
    var b = parentBucket(r);
    (children[b] = children[b] || []).push(r);
  });
  Object.keys(children).forEach(function (b) { ticketSort(children[b]!, col, dir); });

  var out: TicketTreeEntry[] = [], seen: Record<string, boolean> = {};
  // Cycle-safe by construction: `seen` gates every push, so a parent chain that
  // loops (Jira allows an epic to be re-parented) terminates instead of recursing
  // forever — the same guard featureCostTree carries.
  function walk(bucket: string, depth: number): void {
    (children[bucket] || []).forEach(function (r) {
      if (seen[r.id]) return;
      seen[r.id] = true;
      out.push({ node: r, depth: depth });
      walk(r.id, depth + 1);
    });
  }
  walk('', 0);
  // Anything unreached (its parent is present but formed a cycle, or it was
  // bucketed under a row that itself never got walked) still gets listed.
  var rest = rows.filter(function (r) { return !seen[r.id]; });
  ticketSort(rest, col, dir);
  rest.forEach(function (r) { seen[r.id] = true; out.push({ node: r, depth: 0 }); });
  return out;
}

// ---- Collapse / expand -------------------------------------------------------
//
// 93 tickets over three levels is more than a reader wants at once, and the depth
// that matters is the phase (epic → story): a story's subtasks are detail you open
// deliberately. So the tree ships folded from depth 1 down, and the fold is a view
// concern only — every function below reads a FLATTENED, depth-ordered tree
// (`ticketTree`'s output) and never re-derives the parent edges.

/**
 * How many rows sit beneath each row — its whole subtree, not just direct children.
 *
 * Zero doubles as "this row is a leaf", so it drives both the toggle's presence and
 * the "+N" a collapsed row shows. Reads the flattened order rather than the parent
 * map: in a depth-ordered list a row's descendants are exactly the run of following
 * rows deeper than it, which is also true of the SEARCHED tree, where a parent's
 * children may have been filtered away. O(n²) worst case on a fully-nested list, at
 * n ≈ 93 rows and only on re-render.
 */
export function ticketDescendantCount(entries: TicketTreeEntry[]): Record<string, number> {
  var out: Record<string, number> = {};
  for (var i = 0; i < entries.length; i++) {
    var e = entries[i]!, n = 0;
    for (var j = i + 1; j < entries.length && entries[j]!.depth > e.depth; j++) n++;
    out[e.node.id] = n;
  }
  return out;
}

/**
 * The initial fold: every row with children at depth >= `expandThrough` starts
 * collapsed, so depths 0..expandThrough are what a reader first sees.
 *
 * Leaves are deliberately absent from the set rather than present-and-false — a
 * leaf has nothing to fold, and putting one in would make it grow a toggle if the
 * connector later reveals a child.
 */
export function defaultCollapsed(entries: TicketTreeEntry[], expandThrough: number): Record<string, boolean> {
  var kids = ticketDescendantCount(entries);
  var out: Record<string, boolean> = {};
  entries.forEach(function (e) {
    if (kids[e.node.id] && e.depth >= expandThrough) out[e.node.id] = true;
  });
  return out;
}

/**
 * Drop the rows a collapsed ancestor hides. Depth-ordered input, so one cursor is
 * enough: collapsing at depth d skips every following row deeper than d, and the
 * first row back at depth <= d ends the run. Collapsing a parent therefore hides
 * its whole subtree, not just the level below it — no need to also fold the
 * children, whose own state is remembered for when the parent reopens.
 */
export function visibleTicketEntries(entries: TicketTreeEntry[], collapsed: Record<string, boolean>): TicketTreeEntry[] {
  var out: TicketTreeEntry[] = [], cut = -1;
  entries.forEach(function (e) {
    if (cut >= 0 && e.depth > cut) return;
    cut = collapsed[e.node.id] ? e.depth : -1;
    out.push(e);
  });
  return out;
}

/**
 * Keep every row that matches, PLUS the ancestors needed to reach it, so a search
 * hit deep in the tree still renders in place rather than as an orphan. Ancestors
 * pulled in this way are the branch structure of the result, not extra hits.
 */
export function ticketFilterKeepingAncestors(rows: TicketRow[], q: string): TicketRow[] {
  var term = (q || '').trim();
  if (!term) return rows.slice();
  var byKey: Record<string, TicketRow> = {};
  rows.forEach(function (r) { selfKeys(r).forEach(function (k) { byKey[k] = r; }); });
  var keep: Record<string, boolean> = {};
  rows.forEach(function (r) {
    if (!ticketMatches(r, term)) return;
    keep[r.id] = true;
    // Walk up, guarding against a cycle with a visit set.
    var cur: TicketRow | undefined = r, hops: Record<string, boolean> = {};
    while (cur && !hops[cur.id]) {
      hops[cur.id] = true;
      var pid: string | null | undefined = cur.parentId;
      var next: TicketRow | undefined = (pid && byKey[pid]) || (cur.parentKey ? byKey[cur.parentKey] : undefined);
      if (!next || next === cur) break;
      keep[next.id] = true;
      cur = next;
    }
  });
  return rows.filter(function (r) { return keep[r.id]; });
}
